import React from 'react';
import { PagePackState } from '../../../types/api/PagePackState';
import { BADGE_STATUS, StatusBadge } from '../../../_lib/components/Badge';
import { LinkedTeaserImage } from '../../../components/molecules/LinkedTeaserImageInLists';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';
import { DateFormat } from '../../../_lib/components/DateFormat';
import { Hyperlink, HyperlinkTarget } from '../../../components/atoms/Hyperlink';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { ColumnIdentifier } from '../../../components/organisms/Table';
import { ContentAttachmentList } from '../../../components/molecules/ContentAttachmentList/ContentAttachmentList';
import { ExtendedInfoSVG } from '../../../_lib/components/ExtendedInfo';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { InvisiblyDefinedTerm } from '../../../_lib/components/InvisiblyDefinedTerm';
import { getDisplayedTextByConfig } from '../PageListDetails.utils';
import { DisplayOptions, PageListTeaserPage } from '../PageListDetails.types';

type PageListTeaserItemProps = {
  page: PageListTeaserPage;
  showColumn: (any: string) => boolean;
  displayOptions: DisplayOptions;
};

const getArchivedBadge = (page: PageListTeaserPage) => {
  return page.archived ? <StatusBadge status={BADGE_STATUS.ARCHIVED} archiveDate={page.archiveDate} /> : null;
};

export function PageListTeaserItem({ page, showColumn, displayOptions }: PageListTeaserItemProps) {
  const { t } = useSonTranslation('glossary');
  const description = getDisplayedTextByConfig(page, displayOptions.displayedInformationType);
  return (
    <li
      className={`page-list-teaser__list-item${
        page.categories && page.categories.length > 0 && page.categories.at(0)?.hexColor
          ? ' page-list-teaser__list-item--category-border'
          : ''
      }`}
      style={
        page.categories && page.categories.length > 0 ? { borderLeftColor: page.categories.at(0)?.hexColor } : undefined
      }
      key={page.pageLink.name}
    >
      {page.teaserImage && (
        <div className="page-list-teaser__item-image-container">
          <LinkedTeaserImage imageSrc={page.teaserImage.url} pageLink={page.pageLink.url} />
        </div>
      )}
      <article className="page-list-teaser__item-main-container">
        <div className="page-list-teaser__item-text-container">
          <div className="page-list-teaser__item-headline">
            <div>
              {page.categories && page.categories.length > 0
                ? (
                    <InvisiblyDefinedTerm
                      className="page-list-teaser__category font-regular-13-13"
                      definition={t(`category${page.categories.length > 1 ? '_plural' : ''}`)}
                    >
                      {page.categories.map(el => el.title).join(', ')}
                    </InvisiblyDefinedTerm>
                  )
                : null}
              <ContentHeadline
                hierarchy={3}
                url={page.pageLink.url}
                title={page.pageLink.name}
                modifier="no-margin-bottom"
                stateIndicatorStatus={page.packState === PagePackState.ONLY_DRAFT_VERSION ? 'red' : undefined}
              />
            </div>
            {page.integrated
              ? (
                  <ExtendedInfoSVG
                    helpText={t('integratedPage')}
                    tooltipPlacement={TooltipPlacement.RIGHT}
                    svgSpriteID={Icons.CONNECTIONS}
                  />
                )
              : null}
            {getArchivedBadge(page)}
          </div>
          {description && (
            <div className="page-list-teaser__content-paragraph">
              <ContentParagraph>{description}</ContentParagraph>
            </div>
          )}
          {page.contentDate &&
            <DateFormat className="page-list-teaser__date font-regular-14-19" date={page.contentDate} />}
          {page.externalLink && (
            <p className="page-list-teaser__link-container">
              <Hyperlink
                className="page-list-teaser__external-link font-regular-14-19"
                to={page.externalLink.url}
                external
                target={HyperlinkTarget.BLANK}
              >
                <SVG svgClass="page-list-teaser__external-link-icon" svgSpriteId={Icons.EXTERNAL_LINK_ARROW} />
                {page.externalLink.name}
              </Hyperlink>
            </p>
          )}
        </div>
        {typeof page.attachments !== 'undefined' &&
          page.attachments.length > 0 &&
          showColumn(ColumnIdentifier.attachment) && (
          <div className="page-list-teaser__item-attachments-container">
            <ContentAttachmentList
              attachments={page.attachments}
              hasMoreAttachments={page.hasMoreAttachments}
              moreAttachmentsUrl={page.pageLink}
            />
          </div>
        )}
      </article>
    </li>
  );
}
